<template>
  <div
    v-if="hasDisconnected"
    id="notification_banner"
    class="w-full px-2 py-2 border-b flex justify-center items-center proxima_nova text_color text-sm font-medium"
  >
    <div class="flex items-center gap-4">
      <div class="flex items-center gap-2">
        <ThemeImage :icon="imageNames.WARNING" class="size-5" />
        <span>
          Reconnect your
          {{ formatVendorName(primaryDisconnectedAccount.vendor) }} account
          <span class="font-semibold">{{
            primaryDisconnectedAccount.text
          }}</span>
        </span>
      </div>
      <button
        class="font-semibold px-3 py-1 primary_medium"
        @click="reconnectMailbox(primaryDisconnectedAccount.data)"
      >
        Reconnect
      </button>
    </div>

    <div class="absolute right-4 flex items-center">
      <div v-if="disconnectedAccounts.length > 1" class="relative mr-4">
        <CustomDropdown
          ref="dropdownRef"
          gap="0px"
          position="end"
          dropdownCSS="p-0"
          :relative-to-viewport="true"
          :overflow-y="overflowOptions.VISIBLE"
          @close="handleDropdownClose"
          @triggerClick="handleTriggerClick"
        >
          <template #trigger>
            <button class="flex items-center gap-1 font-semibold">
              View {{ disconnectedAccounts.length - 1 }} more
              <ThemeImage
                :icon="imageNames.DROPDOWN_GREY"
                class="size-5"
                :class="{ 'rotate-180': isDropdownOpen }"
              />
            </button>
          </template>

          <template #default>
            <div class="w-96 rounded-lg shadow-lg p-2">
              <div
                v-for="(account, index) in disconnectedAccounts.slice(1)"
                :key="account.id"
              >
                <div class="flex justify-between items-start gap-2 px-3 py-2">
                  <div
                    class="size-7 flex-shrink-0 flex items-center justify-center mt-2 rounded-full vendor_icon"
                  >
                    <ThemeImage
                      :icon="vendorIcons[account.vendor]"
                      class="size-4"
                    />
                  </div>
                  <div
                    class="flex flex-col justify-between pt-2 pb-2 gap-[6px]"
                  >
                    <p class="text_color">
                      Reconnect your
                      {{ formatVendorName(account.vendor) }} account
                      <span class="font-semibold">{{ account.text }}</span>
                    </p>
                    <button
                      class="font-semibold px-3 py-1 w-fit primary_medium"
                      @click="reconnectMailbox(account)"
                    >
                      Reconnect {{ formatVendorName(account.vendor) }}
                    </button>
                    <p class="secondary_color text-xs">
                      {{ timeAgo(account.timestamp) }}
                    </p>
                  </div>

                  <div
                    class="w-4 flex-shrink-0 flex items-center justify-center mt-2"
                  >
                    <button @click.stop="dismissAccount(index + 1)">
                      <ThemeImage
                        :icon="imageNames.CROSS_GREY"
                        class="size-4"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </CustomDropdown>
      </div>

      <div
        class="cursor-pointer flex items-center justify-center size-5"
        @click="handleEmailDismiss"
      >
        <ThemeImage class="size-5" :icon="imageNames.CROSS_GREY" />
      </div>
    </div>
  </div>
  <div
    v-if="isBillingDue"
    id="notification_banner"
    class="w-full px-2 py-2 border-b flex justify-center items-center proxima_nova text_color text-sm font-medium"
  >
    <div class="flex items-center gap-4">
      <div class="flex items-center gap-2">
        <span v-if="billingOverDueSince < 14">
          Your payment is due. Please approve the payment to continue using
          SuperAGI.
        </span>
        <span v-else-if="billingOverDueSince >= 14 && billingOverDueSince < 30">
          Your payment is due. If the payment is not processed by
          {{ billingEndDate }} you will be downgraded to a Free Plan.
        </span>
        <span v-else-if="billingOverDueSince >= 30 && billingOverDueSince < 45">
          You have been downgraded to free plan due to unpaid invoices.
        </span>
      </div>
      <button
        class="font-semibold px-3 py-1 primary_medium"
        @click="openPaymentRetryLink"
      >
        Pay Now
      </button>
    </div>

    <div class="absolute right-4 flex items-center">
      <div
        v-if="billingOverDueSince <= 30"
        class="cursor-pointer flex items-center justify-center size-5"
        @click="handlePaymentDismiss"
      >
        <ThemeImage class="size-5" :icon="imageNames.CROSS_GREY" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import DashboardService from "@/src/api/DashboardService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { emailVendorIcons } from "@/src/constants/settingConstants.js";
import { overflowOptions } from "@/src/constants/customComponentConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import { timeAgo } from "@/src/utils/utils.js";
import store from "@/src/store/index.js";
import { message } from "ant-design-vue";
import BillingService from "@/src/api/BillingService.js";
import { useRouter } from "vue-router";

const router = useRouter();
const disconnectedAccounts = ref([]);
const isDropdownOpen = ref(false);

const hasDisconnected = computed(() => disconnectedAccounts.value.length > 0);

const isBillingDue = computed(() => store.getters["billing/paymentDue"]);
const billingEndDate = computed(() => store.getters["billing/billingEndDate"]);
const billingOverDueSince = computed(
  () => store.getters["billing/paymentOverdueSince"],
);

const openPaymentRetryLink = () => {
  let link = store.getters["billing/paymentRetryLink"];
  window.open(link, "_blank");
};

const primaryDisconnectedAccount = computed(() => {
  const account = disconnectedAccounts.value?.[0];

  return {
    vendor: account?.vendor?.toLowerCase?.() || "email",
    text: account?.text || "",
    data: account || null,
  };
});

// Function to format vendor name properly
const formatVendorName = (vendor) => {
  if (vendor.toLowerCase() === "linkedin") {
    return "LinkedIn";
  }
  return vendor.toLowerCase();
};

// Extended emailVendorIcons to include LinkedIn
const vendorIcons = {
  ...emailVendorIcons,
  LINKEDIN: imageNames.LINKEDIN,
};

const handleDropdownClose = () => {
  isDropdownOpen.value = false;
};

const handleEmailDismiss = () => {
  disconnectedAccounts.value = [];
  const dismissInfo = {
    dismissedAt: new Date().toISOString(),
  };
  localStorage.setItem("crossNotificationBanner", JSON.stringify(dismissInfo));
  if (!isBillingDue.value) {
    store.dispatch("dynamicBanner/updateNotificationBanner", false);
  }
};

const handlePaymentDismiss = () => {
  const dismissInfo = {
    dismissedAt: new Date().toISOString(),
  };
  localStorage.setItem(
    "paymentNotificationBanner",
    JSON.stringify(dismissInfo),
  );
  store.dispatch("billing/setPaymentDue", {
    paymentDue: false,
    retryLink: "",
    overdueSince: 0,
    billingEndDate: "",
  });
  if (!hasDisconnected.value) {
    store.dispatch("dynamicBanner/updateNotificationBanner", false);
  }
};

const handleTriggerClick = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const fetchPrimaryDisconnectedMailboxesAndLinkedin = async () => {
  const shouldShow = shouldShowNotificationBanner();
  if (!shouldShow) {
    disconnectedAccounts.value = [];
    await store.dispatch("dynamicBanner/updateNotificationBanner", false);
    return;
  }

  try {
    const response = await DashboardService.fetchPersonalMailboxes();
    if (response.success) {
      const emailAccounts = response.email_accounts
        .filter((mailbox) => mailbox.status === "DISCONNECTED")
        .map((mailbox) => ({
          id: mailbox.id,
          text: mailbox.email,
          vendor: mailbox.vendor,
          status: mailbox.status,
          full_name: mailbox.full_name,
          timestamp: mailbox.updated_at,
        }));

      // Fetch LinkedIn account status separately
      let linkedInAccount = null;
      try {
        const linkedInResponse =
          await DashboardService.getPrimaryLinkedInAccount({
            is_primary: true,
          });
        // Only add LinkedIn if success is false and is_disconnected_account_present is true
        if (
          !linkedInResponse.success &&
          linkedInResponse.is_disconnected_account_present
        ) {
          linkedInAccount = {
            id: linkedInResponse.id,
            text: linkedInResponse.email || "LinkedIn Account",
            vendor: "LINKEDIN",
            status: "DISCONNECTED",
            full_name: linkedInResponse.name || "LinkedIn Account",
            timestamp: linkedInResponse.updated_at || new Date().toISOString(),
          };
        }
      } catch (linkedInError) {
        console.error("Error fetching LinkedIn account:", linkedInError);
      }

      // Combine email and LinkedIn accounts
      // If email list is empty, show only LinkedIn, otherwise add LinkedIn to the list
      if (emailAccounts.length === 0 && linkedInAccount) {
        disconnectedAccounts.value = [linkedInAccount];
      } else if (emailAccounts.length > 0) {
        disconnectedAccounts.value = linkedInAccount
          ? [...emailAccounts, linkedInAccount]
          : emailAccounts;
      } else {
        disconnectedAccounts.value = [];
      }

      // Only update if billing is not due, to avoid overriding billing notifications
      if (!isBillingDue.value && disconnectedAccounts.value.length > 0) {
        store.dispatch("dynamicBanner/updateNotificationBanner", true);
      }
    }
  } catch (error) {
    console.error("Error fetching mailboxes:", error);
  }
};

const reconnectMailbox = async (account) => {
  try {
    if (!account) return;

    if (account.vendor === "LINKEDIN") {
      const response = await DashboardService.connectPrimaryLinkedInAccount({
        is_primary: true,
      });
      if (response.success && response.redirect_url) {
        window.open(response.redirect_url);
      } else {
        message.error("Failed to connect LinkedIn account");
      }
    } else if (account.vendor === "SMTP") {
      router.push({
        name: 'settings',
        hash: '#personal_mailboxes',
        query: { reconnect_smtp_id: account.id }
      });
    } else {
      const response = await DashboardService.connectPersonalMailbox(
        account.vendor,
      );
      if (response.success && response.redirect_url) {
        window.open(response.redirect_url);
      }
    }
  } catch (error) {
    console.error("Error reconnecting mailbox:", error);
  }
};

const shouldShowNotificationBanner = (
  key = "crossNotificationBanner",
  thresholdHours = 24,
) => {
  const dismissedData = localStorage.getItem(key);
  if (dismissedData) {
    try {
      const { dismissedAt } = JSON.parse(dismissedData);
      const dismissedTime = new Date(dismissedAt).getTime();
      const now = Date.now();
      const hoursPassed = (now - dismissedTime) / (1000 * 60 * 60);

      return hoursPassed >= thresholdHours;
    } catch (e) {
      console.warn("Invalid dismissal data:", e);
      return true;
    }
  }
  return true;
};

const dismissAccount = (index) => {
  disconnectedAccounts.value.splice(index, 1);
  if (disconnectedAccounts.value.length <= 1) {
    isDropdownOpen.value = false;
  }
};

const checkPaymentDue = async () => {
  try {
    const response = await BillingService.checkPaymentDue();
    console.log("check payment due response");
    console.log(response);
    if (response?.success && response?.payment_failed === true) {
      const shouldShowPaymentBanner = shouldShowNotificationBanner(
        "paymentNotificationBanner",
        24,
      );
      if (shouldShowPaymentBanner) {
        store.dispatch("billing/setPaymentDue", {
          paymentDue: true,
          retryLink: response?.retry_link,
          overdueSince: response?.overdue_since,
          billingEndDate: response?.billing_end_date,
        });
        await store.dispatch("dynamicBanner/updateNotificationBanner", true);
      } else if (disconnectedAccounts.value.length === 0) {
        await store.dispatch("dynamicBanner/updateNotificationBanner", false);
      }
    }
    console.log("billing overdue since");
    console.log(billingOverDueSince);
  } catch (error) {
    console.error("Error fetching billing version:", error);
  }
};

onMounted(() => {
  fetchPrimaryDisconnectedMailboxesAndLinkedin();
  checkPaymentDue();
  console.log("Billing due");
  console.log(isBillingDue.value);
});
</script>

<style scoped>
#notification_banner {
  background: var(--notification-banner-bg);
  border-top: 1px solid var(--notification-banner-border);
  border-bottom: 1px solid var(--notification-banner-border);
}

.vendor_icon {
  background-color: var(--white-opacity-20);
}
</style>
